import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { theme } from "../theme.js";
import LoadingOverlay from "../components/LoadingOverlay.jsx";
import { getOrgIdFromStorage } from "../utils/storage.js";
import { API_URL } from "../utils/constants.js";

import InfoBox from "../components/base/SmallItem.jsx";
import RaceItem from "../components/raceItem.jsx";
import {HeroReadOnly} from "../components/heroSection.jsx";
import AboutItem from "../components/aboutItem.jsx";
import Section from "../components/base/Section.jsx";
import AddRace from "../components/addRace.jsx";
import StickyColumn from "../components/StickyColumn.jsx";
import Button from "../components/base/Button.jsx";

import { CalendarIcon, GlobeAltIcon, MapPinIcon, MegaphoneIcon, BuildingLibraryIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { icon } from "leaflet";

export default function EventDetail({events}) {

  const loc = useLocation();
  const navigate = useNavigate();
  const { event_id } = useParams();
  
  const [races, setRaces] = useState([]);
  const [event, setEvent] = useState(null);
  const [organizer, setOrganizer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const eventFromNav = loc.state?.event;
  const refreshEvent = loc.state?.refresh;
  const orgId = getOrgIdFromStorage();
  
  // Trigger refresh when coming from race creation/update
  useEffect(() => {
    if (refreshEvent === true) {
      setLoading(true);
      setRefreshTrigger(prev => prev + 1);
    }
  }, [refreshEvent]);
  
  // Optimized: Consolidated event, organizer, and races fetching
  useEffect(() => {
    let isMounted = true;
    
    const fetchAllData = async () => {
      try {
        // Fetch event
        const eventRes = await fetch(`${API_URL}/api/events?event_id=${event_id}`);
        if (!eventRes.ok) throw new Error(`HTTP ${eventRes.status}`);
        const eventData = await eventRes.json();
        
        if (!isMounted) return;
        
        // Parse event data (handle both array and object responses)
        let parsedEvent = null;
        if (Array.isArray(eventData) && eventData.length > 0) {
          parsedEvent = eventData[0];
        } else if (eventData && typeof eventData === 'object' && eventData.id_bdd) {
          parsedEvent = eventData;
        }
        
        if (parsedEvent) {
          setEvent(parsedEvent);
          
          // Use races from event response if available, otherwise fetch separately
          if (parsedEvent.races && Array.isArray(parsedEvent.races)) {
            setRaces(parsedEvent.races);
          } else if (parsedEvent.id_bdd) {
            // Fetch races if not included in event
            const racesRes = await fetch(`${API_URL}/api/races?event_id=${parsedEvent.id_bdd}`);
            if (racesRes.ok) {
              const racesData = await racesRes.json();
              if (isMounted) setRaces(racesData || []);
            }
          }
          
          // Fetch organizer in parallel
          if (parsedEvent.organizer_id) {
            fetch(`${API_URL}/api/organizers/${parsedEvent.organizer_id}`)
              .then(res => res.ok ? res.json() : null)
              .then(data => { if (isMounted && data) setOrganizer(data); })
              .catch(err => console.error("Error fetching organizer:", err));
          }
        } else {
          setEvent(eventFromNav);
        }
      } catch (err) {
        console.error("Error fetching event:", err);
        setEvent(eventFromNav);
      }
    };
    
    if (event_id) {
      fetchAllData();
    }
    
    return () => {
      isMounted = false;
    };
  }, [event_id, refreshTrigger, eventFromNav]);
  
  const id = event?.id_bdd || event?.event_id;
  
  // Optimized: Only set loading to false when event is loaded (organizer is optional)
  useEffect(() => {
    if (event) {
      setLoading(false);
    }
  }, [event]);

  if (error) return <p>Error: {error}</p>;
  if (!event) return <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><p>Event not found</p></div>;

  const location = event.location || "Unknown location";
  const country = event.country || "";
  const start = event.start_date
    ? new Date(event.start_date).toLocaleDateString()
    : "Date TBD";
  const end = event.end_date
    ? new Date(event.end_date).toLocaleDateString()
    : "Date TBD";
  const title = event.name || "Untitled Event";
  const image = event.image && event.image.trim() ? event.image : null;
  const type = event.sport || "Evènement";  
  const eventOrgId = event.organizer_id;
  
  const handleModifyEvent = () => {
    navigate('/create-event', { state: { editEvent: event } });
  };

  const handlePublishEvent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/publish/${event.id_bdd}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: true }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updatedEvent = await res.json();
      setEvent(updatedEvent);
    } catch (err) {
      console.error("Error publishing event:", err);
      setError("Failed to publish event");
    }
  };

  // Shared summary content
  const summaryContent = (
    <>
      <div style={styles.summaryItem}><InfoBox><CalendarIcon style={styles.icon} /> {start} - {end}</InfoBox></div>
      <div style={styles.summaryItem}>
        <InfoBox><MapPinIcon style={styles.icon} /> 
          {location && country ? (`${location}, ${country}`) : location ? location : country ? country : "Location TBD"}
        </InfoBox></div>
      <div style={styles.summaryItem}><InfoBox><BuildingLibraryIcon style={styles.icon} /> {organizer?.name || 'Organizer'}</InfoBox></div>
      {event?.website && (
        <div style={styles.summaryItem}>
          <a href={event.website.startsWith('http') ? event.website : 'https://' + event.website} target="_blank" rel="noopener noreferrer" style={styles.websiteLink}>
            <GlobeAltIcon style={{ ...styles.icon, color: theme.colors.white }} /> Voir le site internet
          </a>
        </div>
      )}
      {orgId && eventOrgId && (
        <div style={styles.buttonsContainer}>
          {parseInt(orgId)===parseInt(eventOrgId) && !event.active && (
            <Button icon={MegaphoneIcon} title="Publier l'événement" onClick={handlePublishEvent} />
          )}
          {parseInt(orgId)===parseInt(eventOrgId) && (
            <Button icon={PencilSquareIcon} title="Modifier l'événement" onClick={handleModifyEvent} />
          )}
        </div>
      )}
    </>
  );

  return (
    <div>
      <HeroReadOnly title={title} type={type} image={image} />
      {/* Mobile collapsible summary */}
      {isMobile && (
        <div style={styles.mobileSummary}>
          <button
            type="button"
            onClick={() => setSummaryOpen(o => !o)}
            style={styles.mobileSummaryToggle}
          >
            <span>Informations de l'événement</span>
            <span style={{ transition: "transform 0.2s", display: "inline-block", transform: summaryOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
          </button>
          {summaryOpen && (
            <div style={styles.mobileSummaryBody}>
              {summaryContent}
            </div>
          )}
        </div>
      )}
      {/* Main Content */}
      <div style={{ ...styles.content, gridTemplateColumns: isMobile ? "1fr" : "1fr 450px" }}>
        <div style={styles.mainColumn}>

          {/* Races List */}
          <Section title="Courses">     
              {races
                .filter(race => parseInt(orgId) === parseInt(eventOrgId) || race.active)
                .sort((a, b) => {
                  const aDistance = a.extra_info ? Object.entries(a.extra_info)
                    .filter(([key]) => key.toLowerCase().includes("distance"))
                    .reduce((sum, [, val]) => sum + (parseFloat(val) || 0), 0) : 0;
                  const bDistance = b.extra_info ? Object.entries(b.extra_info)
                    .filter(([key]) => key.toLowerCase().includes("distance"))
                    .reduce((sum, [, val]) => sum + (parseFloat(val) || 0), 0) : 0;
                  return aDistance - bDistance;
                })
                .map((race, idx) => (
              <RaceItem
                  key={idx}
                  race={race}
                  event_id={id}
                  orgId={eventOrgId}
              />
              ))}
              {parseInt(orgId) === parseInt(eventOrgId) && <AddRace event_id={id} />}
          </Section>

          
          <AboutItem description={event.description || "Aucune description disponible."} showFullDescription={showFullDescription} setShowFullDescription={setShowFullDescription} />

        </div>

        {/* Sticky Right Column — desktop only */}
        {!isMobile && (
          <StickyColumn title="Informations de l'événement" topOffset="50px">
            {summaryContent}
          </StickyColumn>
        )}
      </div>
      <LoadingOverlay isVisible={loading} message="Chargement..." />
    </div>
  );
}

const styles = {
  topMargin: {
    backgroundColor: theme.colors.bgDark,
    height: "80px",
    width: "100%",
  },
  mobileSummary: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 20px 10px",
  },
  mobileSummaryToggle: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.surface || "#fff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontSize: theme.fonts.size.md,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textInverted,
    marginTop : theme.spacing.lg,
  },
  mobileSummaryBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    border: `1px solid ${theme.colors.border}`,
    borderTop: "none",
    borderRadius: `0 0 ${theme.radius.md} ${theme.radius.md}`,
    backgroundColor: theme.colors.surface || "#fff",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "10px 20px",
    display: "grid",
    gap: "40px",
  },
  mainColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "40px",
    minWidth: 0,
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
  },
  buttonsContainer: {
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.lg,
    borderTop: `1px solid ${theme.colors.border}`,
    gap: theme.spacing.md,
    display: "flex",
    flexDirection: "column",
  },
  websiteLink: {
    display: "inline-block",
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.primary || "#007bff",
    color: "white",
    textDecoration: "none",
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    textAlign: "center",
    transition: "background-color 0.2s ease",
  },
  label: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textMuted,
  },
  value: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textInverted,
    margin: "0",
  },
  icon: {
    width: "20px",
    height: "20px",
    color: theme.colors.textMuted,
    verticalAlign: "middle",
    marginRight: theme.spacing.xs,
  },
  buttonIcon: {
    width: "18px",
    height: "18px",
    color: "inherit",
    marginRight: theme.spacing.xs,
    verticalAlign: "middle",
  },
};